import React from 'react';
import { addDays, isAfter, isBefore, setHours, setMinutes, setSeconds } from 'date-fns';
import format from 'date-fns/format';
import { IconButton, Pane, SearchIcon, Strong, Text, TextInput, toaster } from 'evergreen-ui';
import { Line } from 'react-chartjs-2';
import { useLazyGetChannelMessageDataQuery } from 'Services/api/channelMessagesApi';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import { initialParamsState, filterStatus } from 'Features/Stations/Station/Messages/messageUtils';
import { useUrlParams } from 'Utils/useUrlParams';
import { useGetVocStatusesByFilterQuery } from 'Services/api/vocStatusesApi';
import MessagesChannel from './MessagesChannel';

const DATE_FORMAT = 'yyyy-MM-dd';

const initialParamsDate = {
  start: format(addDays(setHours(setMinutes(setSeconds(new Date(), 0), 0), 0), -7), DATE_FORMAT),
  end: format(setHours(setMinutes(setSeconds(new Date(), 59), 59), 23), DATE_FORMAT),
};

function ChannelChart({ channel = {} }) {
  const { id } = channel;
  const [date, setDate] = React.useState({
    start: initialParamsDate.start,
    end: initialParamsDate.end,
  });
  const [getData, { data: graphData = [], isLoading, isFetching }] = useLazyGetChannelMessageDataQuery();
  const fetchMessageChannelData = React.useCallback(async () => {
    getData({ id, start: format(new Date(date.start), DATE_FORMAT), end: format(new Date(date.end), DATE_FORMAT) });
  }, [date.end, date.start, getData, id]);

  const onChangeDate = React.useCallback((e) => {
    const { name, value } = e.target;
    setDate((cur) => {
      if (name === 'start' && isAfter(new Date(value), new Date(cur.end))) {
        toaster.danger('Время начала должно быть меньше конечного', { id: name });
        return cur;
      }
      if (name === 'end' && isBefore(new Date(value), new Date(cur.start))) {
        toaster.danger('Время конца должно быть больше начального', { id: name });
        return cur;
      }
      return { ...cur, [name]: value };
    });
  }, []);

  React.useEffect(() => {
    getData({
      id,
      start: format(new Date(initialParamsDate.start), DATE_FORMAT),
      end: format(new Date(initialParamsDate.end), DATE_FORMAT),
    });
  }, [getData, id]);

  if (isLoading || isFetching) return <SuspenseSpinner />;
  return (
    <Pane display="grid" gridTemplateRows="max-content 1fr" gap=".5em">
      <Pane display="grid" justifyContent="center" gridAutoFlow="column" gridAutoColumns="max-content" alignItems="center" columnGap=".5em">
        <Text> с </Text>
        <TextInput
          width="auto"
          type="date"
          name="start"
          onChange={onChangeDate}
          value={date.start}
          max={date.end}
        />
        <Text> по </Text>
        <TextInput
          width="auto"
          type="date"
          name="end"
          onChange={onChangeDate}
          value={date.end}
          max={format(new Date(), 'yyyy-MM-dd')}
        />
        <IconButton
          icon={SearchIcon}
          appearance="primary"
          intent="success"
          onClick={fetchMessageChannelData}
        />
      </Pane>
      <Pane minWidth="100%" position="relative">
        <Line
          width={100}
          height={100}
          data={{
            datasets: [{
              data: graphData,
              // данные
              label: 'Полученные данные, кб',
              fill: false, // заполнение графика к оси X
              // линия
              lineTension: 0.2, // 0 - ломаная линия, выше - скругленная
              borderWidth: 2, // толщина линии
              backgroundColor: 'rgba(75,192,192,0.4)', // цвет квадрата где пометка label
              borderColor: 'rgba(75,192,192,1)', // цвет линии
              hoverBorderWidth: 4, // а хз, при наедении ниче не меняется
              hoverBorderColor: 'red', // аналогично
              borderCapStyle: 'butt', // стиль линии "butt" || "round" || "square";
              borderDash: [], // создает пунктирную линию [1,2,3,4,5,6,7] с
              borderDashOffset: 0.0, // хз, расстояние между чертами линии
              borderJoinStyle: 'round', // общий стиль линии "bevel" || "round" || "miter";
              // Точки
              pointBorderWidth: 1, // ширина границы точки
              pointRadius: 3, // радиус точки
              pointBackgroundColor: 'rgba(75,192,192,1)', // цвет точки и квадратика внутри при наведении на точку
              pointBorderColor: '#fff', // цвет границы точки
              // Точки при наведении
              pointHoverBorderWidth: 1, // радиус точки при наведении
              pointHoverRadius: 5, // радиус точки при наведении
              pointHoverBackgroundColor: 'rgba(75,192,192,1)', // цвет точки при наведении
              pointHoverBorderColor: '#fff', // цвет границы при наведении
              pointHitRadius: 1, // за сколько px возникает событии мыши при наведении на точку
            }],
          }}
          options={
            {
              maintainAspectRatio: false, // позволяет графику соответствовать высоте экрана (false = flex size)
              title: {
                display: false, text: 'title', fontColor: '#234361', fontSize: '20', fontStyle: 'normal',
              },
              legend: {
                display: true,
              },
              indexAxis: 'x',
              scales: {
                y: {
                  suggestedMin: 0,
                  min: 0,
                },
              },
              tooltips: {
                backgroundColor: 'rgba(215, 215, 215, 0.9)',
                titleFontColor: 'rgba(0, 0, 0, 0.65)',
                bodyFontColor: 'rgba(0, 0, 0, 0.65)',
                callbacks: {
                  title(tooltipItem, curData) {
                    // console.log('title tooltipItem.datasetIndex ', tooltipItem)
                    let title = curData.datasets[0].data[tooltipItem[0].index].x || '';// tooltipItem.index
                    title = format(title, 'yyyy.MM.dd HH:mm:ss');
                    return title;
                  },
                  label(tooltipItem) {
                    // console.log('label tooltipItem.datasetIndex ', tooltipItem)
                    return `${tooltipItem.value || ''} кб`;
                  },
                },
              },
            }
          }
        />
      </Pane>
    </Pane>
  );
}

export function ChannelInfo({ channel = {} }) {
  const useGetStatusQuery = () => useGetVocStatusesByFilterQuery({ 'entity-types': ['COMMON', 'MESSAGE'] });

  const { data: statuses = [], isFetching: isLoadingStatuses } = useGetStatusQuery();

  const [urlParams] = useUrlParams({
    status: 'number-array',
  });

  const channels = React.useMemo(() => [channel.id], [channel.id]);

  const filteredStatuses = React.useMemo(() => {
    const tmp = filterStatus(urlParams.status, isLoadingStatuses, statuses);
    if (tmp.length > 0) return tmp;
    return initialParamsState.status;
  }, [isLoadingStatuses, statuses, urlParams.status]);

  const params = {
    date: {
      startDate: urlParams.startDate || initialParamsState.startDate,
      endDate: urlParams.endDate || initialParamsState.endDate,
    },
    size: urlParams.size || initialParamsState.size,
    page: initialParamsState.page,
    datasourceIds: initialParamsState.datasourceIds(),
    status: filteredStatuses,
    programs: urlParams.programs || initialParamsState.programs,
    formats: urlParams.formats || initialParamsState.formats,
    channels: urlParams.channels || initialParamsState.channels([channels]),
    mode: 'table',
  };

  if (isLoadingStatuses) return <SuspenseSpinner />;

  return (
    <Pane
      display="grid"
      gridTemplateRows="max-content repeat(2, 1fr)"
      gap=".5em"
      height="100%"
    >
      <Pane justifyContent="center" display="grid" alignItems="center">
        <Strong>{channel.description}</Strong>
      </Pane>
      <ChannelChart
        channel={channel}
      />
      <MessagesChannel
        params={params}
        useGetStatusQuery={useGetStatusQuery}
      />
    </Pane>
  );
}
